// Web push setup shared by the app shell, the header and the enable-prompt.
// iOS only allows push for the Home Screen app, and only shows the permission
// prompt when requestPermission() is called directly from a tap.

export const VAPID_PUBLIC_KEY =
  "BMWj-ENsRfCbM1m5vEc101nZbLXItODeLv1H-1q1KdyND6tNlouQcBpmh9sreMVLzmI9iXb5hOnXs_BLGSdUh84";

const SW_URL = "/service-worker.js";
const PUSH_SUBSCRIPTION_CACHE_KEY = "push_subscription_payload_v1";
const PUSH_SUBSCRIPTION_USER_KEY = "push_subscription_user_v1";

const safeGet = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); the sync just repeats next time.
  }
};

export const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  // iPadOS reports itself as a Mac.
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.navigator.standalone === true ||
  window.matchMedia?.("(display-mode: standalone)").matches === true;

// "needs-install": iPhone/iPad in a Safari tab, push only works from the Home Screen app.
export function getPushState() {
  if (isIOS() && !isStandalone()) return "needs-install";
  if (
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !("Notification" in window)
  ) {
    return "unsupported";
  }
  return Notification.permission; // "default" | "granted" | "denied"
}

// Earlier versions registered with scope "./", which created extra
// registrations per path (e.g. /cases/). Keep only the root one.
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  const rootScope = `${window.location.origin}/`;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations
        .filter((registration) => registration.scope !== rootScope)
        .map((registration) => registration.unregister())
    );
  } catch (error) {
    console.warn("[push] Could not clean up service workers", error);
  }
  return navigator.serviceWorker.register(SW_URL, {
    scope: "/",
    updateViaCache: "none",
  });
}

const urlBase64ToUint8Array = (base64) => {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (char) => char.charCodeAt(0));
};

const sameKey = (buffer, expected) => {
  if (!buffer) return true; // Some browsers don't expose it; assume ours.
  const actual = new Uint8Array(buffer);
  return (
    actual.length === expected.length &&
    actual.every((byte, index) => byte === expected[index])
  );
};

// Subscribes (or re-subscribes) this browser and stores the subscription on
// the server. Requires permission to be granted already.
export async function syncPushSubscription(axiosPrivate, userEmail) {
  if (getPushState() !== "granted" || !userEmail) return false;

  await registerServiceWorker();
  const registration = await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

  let subscription = await registration.pushManager.getSubscription();
  if (
    subscription &&
    !sameKey(subscription.options?.applicationServerKey, applicationServerKey)
  ) {
    // Created with an old key: pushes to it can never be delivered.
    await subscription.unsubscribe();
    subscription = null;
  }
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });
  }

  const serialized = JSON.stringify(subscription);
  if (
    safeGet(PUSH_SUBSCRIPTION_USER_KEY) === userEmail &&
    safeGet(PUSH_SUBSCRIPTION_CACHE_KEY) === serialized
  ) {
    return true;
  }

  await axiosPrivate.post(
    "/subscription",
    { subscription },
    { headers: { "Content-Type": "application/json" }, withCredentials: true }
  );
  safeSet(PUSH_SUBSCRIPTION_CACHE_KEY, serialized);
  safeSet(PUSH_SUBSCRIPTION_USER_KEY, userEmail);
  return true;
}

// Must be the first await inside a click/tap handler, or iOS won't prompt.
export async function requestPushPermission() {
  if (!("Notification" in window)) return "unsupported";
  return Notification.requestPermission();
}
